/**
 * Eigene-Felder-Sub-Editor (W2.1, Schicht 3) — 1:1 aus dem alten Drawer in widgets-tab.tsx
 * gehoben (dort Zeilen ~772-856 vor dem Umbau). Modell-Konstanten (FREIE_TYP_LABEL,
 * MAX_FREIE_FELDER) bleiben bewusst in widgets-tab.tsx stehen (Drift-Ratsche
 * apps/api/test/widget-formular.test.ts scannt genau diese Datei als Text) und werden hier nur
 * importiert.
 */
import { FREIE_TYP_LABEL, MAX_FREIE_FELDER } from "../../../widgets-tab";
import { Checkbox, Icon, Input, Select, Textarea } from "../../../../ui/components";
import type { WidgetEditor } from "../../useWidgetEditorForm";

export function CustomFieldEditor({ editor }: { editor: WidgetEditor }) {
  const { freieFelder } = editor.form;
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <p className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
          Eigene Felder ({freieFelder.length}/{MAX_FREIE_FELDER})
        </p>
        <button
          type="button"
          className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline disabled:opacity-50"
          disabled={freieFelder.length >= MAX_FREIE_FELDER}
          onClick={editor.feldHinzufuegen}
        >
          <Icon name="add" className="text-[16px]" /> Feld hinzufügen
        </button>
      </div>
      {freieFelder.length === 0 ? (
        <p className="mt-1 font-label-md text-label-md text-on-surface-variant">
          Noch keine eigenen Felder. Eigene Felder landen als Zusatzinfo bei der Anfrage; mit dem Typ „Datei-Upload“ kann ein
          Gast außerdem eine Datei anhängen.
        </p>
      ) : (
        <ul className="mt-2 space-y-3">
          {freieFelder.map((z, i) => (
            <li key={i} className="rounded-xl bg-surface-container-lowest p-3 dark:bg-white/[0.03]">
              <div className="flex flex-wrap items-end gap-2">
                <label className="min-w-[180px] flex-1">
                  <span className="mb-1 block font-label-md text-label-md text-on-surface-variant">Beschriftung</span>
                  <Input
                    maxLength={80}
                    placeholder="z. B. Wie sind Sie auf uns aufmerksam geworden?"
                    value={z.label}
                    onChange={(e) => editor.feldAendern(i, { label: e.target.value })}
                  />
                </label>
                <label className="w-[170px]">
                  <span className="mb-1 block font-label-md text-label-md text-on-surface-variant">Typ</span>
                  <Select
                    ariaLabel="Typ"
                    value={z.typ}
                    onValueChange={(v) => editor.feldAendern(i, { typ: v })}
                    options={Object.entries(FREIE_TYP_LABEL).map(([wert, text]) => ({ value: wert, label: text }))}
                  />
                </label>
                <label className="flex items-center gap-2 pb-2">
                  <Checkbox checked={z.pflicht} onChange={(e) => editor.feldAendern(i, { pflicht: e.target.checked })} />
                  <span className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">Pflicht</span>
                </label>
                {/* Reiner Text-Link-Knopf mit eigener Tailwind-Klasse statt `btn-*` — traegt keine
                    `btn-primary/secondary/ghost`-Klasse, faellt nicht unter die Button-Migration
                    dieses Clusters (dasselbe bereits dokumentierte Nicht-Treffer-Muster wie
                    Icon-/Text-Link-Knoepfe in anderen Batches). */}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 pb-2 font-label-md text-label-md text-error hover:underline"
                  onClick={() => editor.feldEntfernen(i)}
                >
                  <Icon name="delete" className="text-[16px]" /> Entfernen
                </button>
              </div>
              {z.typ === "file" && (
                <p className="mt-2 font-label-md text-label-md text-on-surface-variant">
                  Der Gast kann eine Datei anhängen: PDF, JPEG, PNG oder WebP, höchstens 10 MB. Die Datei hängt danach an der
                  Anfrage; der Dateiname steht bei den Zusatzinfos.
                </p>
              )}
              {z.typ === "select" && (
                <label className="mt-2 block">
                  <span className="mb-1 block font-label-md text-label-md text-on-surface-variant">Optionen (eine je Zeile)</span>
                  <Textarea
                    rows={3}
                    placeholder={"Empfehlung\nGoogle\nInstagram"}
                    value={z.optionenText}
                    onChange={(e) => editor.feldAendern(i, { optionenText: e.target.value })}
                  />
                </label>
              )}
              <p className="mt-2 font-label-md text-label-md text-on-surface-variant">
                Schlüssel: <code>{z.key || "—"}</code>
                {z.keyFest
                  ? " · bleibt beim Umbenennen erhalten, damit alte Anfragen zuordenbar bleiben."
                  : " · wird aus der Beschriftung gebildet und beim Speichern festgeschrieben."}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
